import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Instagram, Menu, X } from 'lucide-react';

// --- Custom Icons ---

export const TikTokIcon = ({ size = 18, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-5.201 1.743l-.002-.001.002.001a2.895 2.895 0 0 1 3.183-4.51v-3.5a6.329 6.329 0 0 0-5.394 10.692 6.33 6.33 0 0 0 10.857-4.424V8.687a8.182 8.182 0 0 0 4.773 1.526V6.79a4.831 4.831 0 0 1-1.003-.104z" />
  </svg>
);

export const XIcon = ({ size = 18, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const NAV_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'Contact', href: '/contact' },
  { label: 'Team', href: '/team' },
  { label: 'Careers', href: '/careers' },
];

// --- Ambient background the glass refracts ---

export const AmbientBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none" aria-hidden="true">
    <div
      className="ambient-blob animate-drift-1"
      style={{ top: '-12%', left: '-8%', width: '48vw', height: '48vw', background: 'radial-gradient(circle, rgba(199,214,245,0.9) 0%, rgba(199,214,245,0) 70%)' }}
    />
    <div
      className="ambient-blob animate-drift-2"
      style={{ top: '20%', right: '-12%', width: '42vw', height: '42vw', background: 'radial-gradient(circle, rgba(227,213,243,0.85) 0%, rgba(227,213,243,0) 70%)' }}
    />
    <div
      className="ambient-blob animate-drift-3"
      style={{ bottom: '-15%', left: '20%', width: '50vw', height: '50vw', background: 'radial-gradient(circle, rgba(210,236,223,0.8) 0%, rgba(210,236,223,0) 70%)' }}
    />
  </div>
);

// --- Floating glass pill nav ---

export const GlassNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className="fixed top-0 inset-x-0 z-50 px-4 md:px-6 pt-4">
      <nav className={`glass-pill mx-auto max-w-6xl flex items-center justify-between pl-6 pr-2.5 md:pl-8 transition-all duration-500 ${scrolled ? 'py-2 bg-white/75' : 'py-2.5'}`}>
        <div className="md:flex-1 flex items-center">
          <Link to="/" className="text-xl font-bold tracking-tighter text-zinc-900 hover:opacity-70 transition-opacity">
            KORSA
          </Link>
        </div>

        {/* Centered Links - Desktop Only */}
        <div className="hidden md:flex flex-1 justify-center">
          <div className="flex text-[10px] uppercase tracking-widest font-bold items-center space-x-10">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className={`transition-colors ${pathname === link.href ? 'text-zinc-900' : 'text-zinc-500 hover:text-zinc-900'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="flex justify-end items-center md:flex-1 gap-2">
          <Link
            to="/order"
            className="inline-flex items-center justify-center rounded-full bg-zinc-900 text-white px-6 py-2.5 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-700 transition-colors shadow-[0_8px_20px_rgba(24,24,27,0.18)]"
          >
            Order
          </Link>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-zinc-900 hover:opacity-70 transition-all duration-300 relative z-50"
            aria-label="Menu"
          >
            <div className="relative w-6 h-6">
              <Menu
                size={24}
                className={`absolute inset-0 transition-all duration-300 ${isOpen ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'}`}
              />
              <X
                size={24}
                className={`absolute inset-0 transition-all duration-300 ${isOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'}`}
              />
            </div>
          </button>
        </div>
      </nav>

      {/* Mobile dropdown */}
      <div
        className={`glass md:hidden mx-auto max-w-6xl mt-2 transition-all duration-500 ease-out overflow-hidden ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 border-transparent shadow-none'
        }`}
      >
        <div className="flex flex-col space-y-4 p-6">
          {NAV_LINKS.map((link, index) => (
            <Link
              key={link.label}
              to={link.href}
              onClick={() => setIsOpen(false)}
              className={`text-[10px] uppercase tracking-widest font-bold transition-all duration-300 transform ${
                pathname === link.href ? 'text-zinc-900' : 'text-zinc-500 hover:text-zinc-900'
              } ${isOpen ? 'translate-x-0 opacity-100' : '-translate-x-4 opacity-0'}`}
              style={{ transitionDelay: isOpen ? `${index * 50}ms` : '0ms' }}
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
};

// --- Glass footer card ---

export const GlassFooter = () => (
  <footer className="px-4 md:px-6 pb-6 mt-24">
    <div className="glass mx-auto max-w-6xl px-8 md:px-12 py-12 md:py-16">
      <div className="grid md:grid-cols-4 gap-12 md:gap-8 mb-16">
        <div className="col-span-1 md:col-span-2">
          <h4 className="text-3xl font-bold tracking-tighter mb-6 text-zinc-900">KORSA</h4>
          <p className="text-zinc-500 max-w-xs text-sm leading-relaxed">
            Building the future of assistive technology
          </p>
        </div>

        <div>
          <ul className="space-y-4 text-xs font-medium text-zinc-500">
            {NAV_LINKS.map((link) => (
              <li key={link.label}>
                <Link to={link.href} className="hover:text-zinc-900 transition-colors">{link.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="flex gap-4 mb-8">
            <a href="https://www.tiktok.com/@korsahq" className="text-zinc-500 hover:text-zinc-900 transition-colors" aria-label="TikTok"><TikTokIcon size={20} /></a>
            <a href="https://x.com/korsahq" className="text-zinc-500 hover:text-zinc-900 transition-colors" aria-label="X (Twitter)"><XIcon size={20} /></a>
            <a href="https://www.instagram.com/@korsahq" className="text-zinc-500 hover:text-zinc-900 transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
          </div>
          <ul className="space-y-4 text-xs font-medium text-zinc-500">
            <li><Link to="/privacypolicy" className="hover:text-zinc-900 transition-colors">Privacy Policy</Link></li>
            <li><Link to="/tos" className="hover:text-zinc-900 transition-colors">Terms of Service</Link></li>
          </ul>
        </div>
      </div>

      <div className="pt-8 border-t border-zinc-900/5 flex flex-col md:flex-row justify-between items-start md:items-end gap-2 text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
        <span>Boston, Massachusetts</span>
        <span>© 2025 Korsa Inc. All Rights Reserved.</span>
      </div>
    </div>
  </footer>
);

// --- Page shell ---

export const GlassPage = ({ children }) => (
  <div className="min-h-screen font-sans text-zinc-900 antialiased selection:bg-zinc-900 selection:text-white overflow-x-clip">
    <AmbientBackground />
    <GlassNav />
    <main>{children}</main>
    <GlassFooter />
  </div>
);
